import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  useColorMode,
  IconButton,
  Progress,
  ScaleFade,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandPointer,
  faExchangeAlt,
  faSave,
  faInfoCircle,
  faCubes,
  faHourglassEnd,
  faCalculator,
  faChartLine,
  faBook,
  faGraduationCap,
  faBolt,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faRocket,
  faBitcoinSign,
  faChartBar,
} from '@fortawesome/free-solid-svg-icons';

export const MetricsTutorial = ({ isOpen, onClose }) => {
  const { colorMode } = useColorMode();
  const [currentSlide, setCurrentSlide] = useState(0);

  const modalBgColor = colorMode === 'dark' ? '#121212' : 'white';
  const modalTextColor = colorMode === 'dark' ? 'white' : '#121212';
  const modalBorderColor = colorMode === 'dark' ? 'white' : '#121212';
  const iconColor = colorMode === 'dark' ? 'orange.400' : 'orange.500';
  const stepBgColor = colorMode === 'dark' ? 'gray.800' : 'gray.100';
  const dotColor = colorMode === 'dark' ? 'gray.600' : 'gray.400';
  const activeDotColor = colorMode === 'dark' ? 'orange.400' : 'orange.500';

  const slides = [
    {
      title: "Welcome to TimechainStats",
      icon: faBitcoinSign,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Your Real-Time Bitcoin Dashboard
          </Text>

          <VStack spacing={4} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBitcoinSign} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Live Bitcoin stats - NO ADS</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Track price, network stats, mining metrics and more - all in real-time
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faRocket} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Fully customizable experience</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Mix and match metrics to create more than 380 million unique dashboards
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faGraduationCap} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Learn while you monitor</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Educational resources, games, and a comprehensive Bitcoin glossary
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={4} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center" mb={2}>
              "It's Up To You!" - Your Journey Starts Here
            </Text>
            <Text fontSize="xs" textAlign="center" opacity={0.8}>
              Use arrow keys to navigate • Press ESC to skip
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Mix & Match Metrics",
      icon: faInfoCircle,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Build your perfect Bitcoin dashboard. Display only the metrics you care about.
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faHandPointer} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Select any metric at the top</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Click on any stat in the top of the app to reveal the Mix & Match menu
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">70+ metrics to choose from</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Combine price indicators, network data, mining statistics, fee analysis, Lightning metrics, and much more
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faSave} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Set it and forget it</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Your custom layout saves automatically and syncs across sessions
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Build Your Own Dashboard
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Recent Blocks Display",
      icon: faCubes,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Watch Bitcoin blocks being mined in real-time
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faCubes} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Live block feed</Text>
                <Text fontSize="sm" opacity={0.8}>
                  See the transactions and more in the latest blocks
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faHourglassEnd} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Color-coded timing</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Green = faster than 10 minutes, Red = slower, Orange border = empty blocks
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faInfoCircle} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Hover for details</Text>
                <Text fontSize="sm" opacity={0.8}>
                  See expected vs actual block times and detailed block information
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Monitor the Chain Your Way
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Latest Block Details",
      icon: faHourglassEnd,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Experience the excitement of new Bitcoin blocks with animated celebrations
          </Text>

          <VStack spacing={3} align="stretch">

<HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Animated celebrations</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Watch tiles glow and bitcoins fall when a new block is mined
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBolt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Real-time updates</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Instant notifications when new blocks are found
                </Text>
              </Box>
            </HStack>

            

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faInfoCircle} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Comprehensive stats</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Block height, hash, size, weight, transactions, and fee details
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Monitor Every New Block
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Bitcoin Calculator",
      icon: faCalculator,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Bitcoin-to-fiat converter with real-time price updates
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faCalculator} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Instant conversions</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Convert between BTC, satoshis, and multiple fiat currencies
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBolt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Real-time prices</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Live price feeds update every second for accurate conversions
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faHandPointer} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Quick access</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Click the calculator icon in the header for instant access
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Calculate Your Stack's Value
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Stack Track",
      icon: faChartBar,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Monitor the real-time value of your holdings in different currencies
          </Text>
          
          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faChartBar} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Track your stack value</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Enter your Bitcoin or satoshi amount to see its current fiat value
                </Text>
              </Box>
            </HStack>
            
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Zero data tracking</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Saves your data on YOUR browser, and deletes when you leave. Timechain Stats has no knowledge of your entries and cannot access the data.
                </Text>
              </Box>
            </HStack>
            
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBolt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Real-time updates</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Watch your stack value change with live price movements
                </Text>
              </Box>
            </HStack>
          </VStack>
          
          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Track Your Bitcoin Journey
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Trading View Charts",
      icon: faChartLine,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Professional-grade Bitcoin price charts with advanced analytics
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faChartLine} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Multiple timeframes</Text>
                <Text fontSize="sm" opacity={0.8}>
                  From 1-minute to monthly candles for any trading style
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Technical indicators</Text>
                <Text fontSize="sm" opacity={0.8}>
                  RSI, MACD, Moving Averages, and dozens more indicators
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faHandPointer} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Full-screen mode</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Click the chart icon in the header for immersive trading view
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Chart Your Way
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Bitcoin Definitions",
      icon: faBook,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Your comprehensive Bitcoin dictionary for learning technical terms and concepts
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBook} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Complete glossary</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Hundreds of Bitcoin and cryptocurrency terms explained
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Search functionality</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Quickly find any term with instant search results
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faHandPointer} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Educational resource</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Click the book icon in the header to start learning
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Master Bitcoin Terminology
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Resources Library",
      icon: faGraduationCap,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Curated collection of the best Bitcoin educational materials and tools
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faGraduationCap} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">15+ categories</Text>
                <Text fontSize="sm" opacity={0.8}>
                  From beginner guides to advanced programming resources
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBook} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Satoshi's writings</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Read the original Bitcoin whitepaper and forum posts
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Tools & wallets</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Recommended software, hardware wallets, and node guides
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Choose Your Learning Path
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Real-Time Features",
      icon: faBolt,
      content: (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" mb={2}>
            Stay connected with live Bitcoin network data and instant notifications
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faBolt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Install the app</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Visit the website and choose 'Add to Home Screen' or 'Install'
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faInfoCircle} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">New block alerts</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Optional notifications when new blocks are mined
                </Text>
              </Box>
            </HStack>

            <HStack align="flex-start" spacing={3}>
              <Box mt={1}>
                <FontAwesomeIcon icon={faExchangeAlt} color={iconColor} />
              </Box>
              <Box>
                <Text fontWeight="bold">Background updates</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Data stays fresh even when you switch tabs
                </Text>
              </Box>
            </HStack>
          </VStack>

          <Box bg={stepBgColor} p={3} borderRadius="md" mt={2}>
            <Text fontSize="sm" fontStyle="italic" textAlign="center">
              "It's Up To You!" - Stay Connected 24/7
            </Text>
          </Box>
        </VStack>
      )
    }
  ];

  const handleDismiss = useCallback(() => {
    localStorage.setItem('timechainStatsTutorialV2Dismissed', 'true');
    onClose();
    setCurrentSlide(0); // Reset to first slide
    // Force scroll to top after modal closes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, [onClose]);

  const goToNextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, slides.length]);

  const goToPrevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        goToNextSlide();
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        goToPrevSlide();
      } else if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentSlide, slides.length, goToNextSlide, goToPrevSlide, handleDismiss]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      size="xl"
      isCentered
      blockScrollOnMount={false}
      preserveScrollBarGap={true}
      returnFocusOnClose={false}
    >
      <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
      <ModalContent
        bg={modalBgColor}
        color={modalTextColor}
        borderWidth="1px"
        borderColor={modalBorderColor}
        borderRadius="6px"
        mx={{ base: 4, md: 0 }}
      >
        <ModalHeader>
          <HStack spacing={2} justify="space-between">
            <HStack spacing={2}>
              <FontAwesomeIcon icon={slides[currentSlide].icon} color={iconColor} />
              <Text fontSize={{ base: "lg", md: "xl" }}>{slides[currentSlide].title}</Text>
            </HStack>
            <IconButton
              icon={<FontAwesomeIcon icon={faTimes} />}
              aria-label="Close tutorial"
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
            />
          </HStack>
          <Progress
            value={(currentSlide + 1) / slides.length * 100}
            size="xs"
            colorScheme="orange"
            mt={2}
            borderRadius="full"
          />
        </ModalHeader>

        <ModalBody py={3}>
          <Box minHeight="320px">
            <ScaleFade in={true} initialScale={0.95}>
              {slides[currentSlide].content}
            </ScaleFade>
          </Box>

          {/* Slide indicator dots */}
          <HStack justify="center" spacing={2} mt={4}>
            {slides.map((_, index) => (
              <Box
                key={index}
                as="button"
                w={2}
                h={2}
                borderRadius="full"
                bg={index === currentSlide ? activeDotColor : dotColor}
                onClick={() => goToSlide(index)}
                transition="all 0.2s"
                _hover={{ transform: 'scale(1.2)' }}
              />
            ))}
          </HStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} justify="space-between" width="100%">
            <Button
              leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={goToPrevSlide}
              isDisabled={currentSlide === 0}
              variant="ghost"
              size="sm"
            >
              Previous
            </Button>

            <Box flex={1} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                {currentSlide + 1} of {slides.length}
              </Text>
            </Box>

            {currentSlide === slides.length - 1 ? (
              <Button
                colorScheme="orange"
                onClick={handleDismiss}
                size="sm"
              >
                Start exploring
              </Button>
            ) : (
              <Button
                rightIcon={<FontAwesomeIcon icon={faChevronRight} />}
                onClick={goToNextSlide}
                colorScheme="orange"
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};